"use client";

import { useSearchParams } from "next/navigation";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { REALMS } from "@/constants/process";
import useChangeRealm from "@/hooks/use-change-realm";
import useCurrentUserManager from "@/hooks/use-current-user-manager";
import { useLocalizedRouter } from "@/hooks/use-localized-router";
import usePersistedBoundStore from "@/store/persisted-bound-store";
import type { RealmSliceState } from "@/store/realm-slice";
import { isAuthenticated } from "@/utils/auth";
import { syncTokensToMiddleware } from "@/utils/auth-cookies";
import { isEvcoreRealm } from "@/utils/process";
import {
  clearSignoutTransitionState,
  clearTabEnvironment,
  clearTabSession,
} from "@/utils/tab-session";
import WaitScreenContent from "../_components/wait-screen-content/wait-screen-content";

// Memoized component to prevent unnecessary re-renders
const WaitScreenContentMemo = memo(WaitScreenContent);

function Signin() {
  // Prevent multiple hook subscriptions causing re-renders
  const realm = usePersistedBoundStore.use.realm();
  const setEnvironment = usePersistedBoundStore.use.setEnvironment();
  const setEnvironmentUrl = usePersistedBoundStore.use.setEnvironmentUrl();
  const setIntentUrl = usePersistedBoundStore.use.setIntentUrl();

  // Cache hook results to prevent re-instantiation
  const changeRealm = useChangeRealm();
  const currentUserManager = useCurrentUserManager();
  const { navigateTo } = useLocalizedRouter();

  // Get navigation data only once
  const searchParams = useSearchParams();

  // Prevent multiple executions and track completion
  const isProcessing = useRef(false);
  const hasCompleted = useRef(false);

  // Capture the intent URL from middleware query parameter
  const nextIntentUrl = useMemo(() => {
    const searchIntentUrl = searchParams.get("intentUrl");
    if (!searchIntentUrl) {
      return undefined;
    }

    if (searchIntentUrl === "/") {
      return undefined;
    }

    return searchIntentUrl;
  }, [searchParams]);

  // Memoize realm check to prevent repeated calculations
  const shouldChangeRealm = useMemo(() => {
    return realm && !isEvcoreRealm(realm);
  }, [realm]);

  // Optimize async operations with aggressive caching
  const handleSignin = useCallback(async () => {
    // Prevent multiple executions and duplicate processing
    if (!currentUserManager || isProcessing.current || hasCompleted.current) {
      return;
    }

    isProcessing.current = true;

    try {
      clearSignoutTransitionState();
      clearTabSession();
      clearTabEnvironment();
      setEnvironment(undefined);
      setEnvironmentUrl(undefined);
      setIntentUrl(nextIntentUrl);

      // Check authentication only once
      const _isAuthenticated = await isAuthenticated(currentUserManager);
      if (_isAuthenticated) {
        // User is authenticated, let the user manager handle the post-auth logic
        // Profile completion check is now handled by the evcore user manager
        syncTokensToMiddleware();
        hasCompleted.current = true;
        navigateTo("/");
        return;
      }

      // Conditionally change realm to reduce operations
      if (shouldChangeRealm) {
        setEnvironment(undefined);
        setEnvironmentUrl(undefined);
        await changeRealm(REALMS.EVCORE as RealmSliceState["realm"]);
        isProcessing.current = false;
        return;
      }

      // Complete the signin process
      hasCompleted.current = true;
      currentUserManager.signinRedirect();
    } catch (error) {
      console.error("Signin error:", error);
      isProcessing.current = false;
      hasCompleted.current = false;
    }
  }, [
    currentUserManager,
    changeRealm,
    navigateTo,
    nextIntentUrl,
    setIntentUrl,
    shouldChangeRealm,
    setEnvironment,
    setEnvironmentUrl,
  ]);

  useEffect(() => {
    handleSignin();
  }, [handleSignin]);

  // Memoize the render to prevent unnecessary DOM updates
  const renderContent = useMemo(
    () => (
      <div className="h-full flex flex-col justify-center items-center space-y-5 text-center">
        <WaitScreenContentMemo />
      </div>
    ),
    []
  );

  return renderContent;
}

// Export memoized component to prevent parent re-renders
export default memo(Signin);
